import { useState, useRef, useEffect } from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Popover from "@mui/material/Popover";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

import { getRoutes } from "routes";
import { getFooterRoutes } from "footer.routes";

import ServiceCard from "./components/ServiceCard";
import ExpandingOptions from "./components/ExpandingOptions";
import MorphGallery from "./components/MorphGallery";
import ScrollToTop from "./components/ScrollToTop";
import styles from "./Presentation.module.css";
import contactStyles from "./Contact.module.css";
import serviceStyles from "./Services.module.css";
import aboutStyles from "./About.module.css";

import bgImage from "assets/images/video-background1.mp4";
import logoVeronica from "assets/images/RealtyOne.png";
import logoOne from "assets/images/logo-veronica.png";
import videoEs from "assets/images/verorealtor-01.mp4";
import videoEn from "assets/images/video-home-english.mp4";
import whatsappIcon from "assets/images/whatsapp.png";
import smsIcon from "assets/images/sms.png";
import mlsLogo from "assets/images/mls.webp";
import equalLogo from "assets/images/equal.webp";
import realtyOneLogo from "assets/images/RealtyOne.png";
import appImage from "assets/images/app.png";
import one1Image from "assets/images/One1.webp";
import realtorLogo from "assets/images/Realtor.webp";
import instagramImg from "assets/images/instagram.png";
import facebookImg from "assets/images/facebook.png";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const WHATSAPP_URL =
  "https://wa.me/12103831687?text=Hola,%20quiero%20más%20información%20sobre%20propiedades";
const SMS_URL = "sms:+12103831687";
const VIDEOS = { es: videoEs, en: videoEn };
const YOUTUBE_CHANNEL_URL = "https://www.youtube.com/@VeronicaFamilyRealtor";
const YOUTUBE_PLAYLIST_ID = "UU85toQJnNX7anRK6IJuYdSQ";

// ─── CONTENT ──────────────────────────────────────────────────────────────────

const content = {
  es: {
    nav: { cta: "Agenda una cita" },
    hero: {
      title: "Veronica Family Realtor",
      slogan: "Soy tu AMIGA, Soy tu REALTOR",
      subtitle: "COMPRA O VENDE TU PROPIEDAD",
      subtitleHighlight: "EN TEXAS",
      cta: "Agenda una cita",
      ctaSub: "Respuesta en menos de 24 horas",
      videoLabel: "Conoce más sobre nosotros",
    },
    stats: [
      { value: "8+", label: "Años de experiencia" },
      { value: "200+", label: "Casas vendidas" },
      { value: "100%", label: "Clientes satisfechos" },
      { value: "24h", label: "Tiempo de respuesta" },
    ],
    services: {
      title: "¿Cómo te ayudamos?",
      subtitle: "Servicios diseñados para cada etapa de tu proceso inmobiliario.",
      items: [
        {
          icon: "home",
          title: "Compra tu hogar",
          description:
            "Te guiamos desde la búsqueda hasta la firma y encontramos la propiedad ideal según tu presupuesto y estilo de vida.",
        },
        {
          icon: "sell",
          title: "Vende más rápido",
          description:
            "Valuación profesional, marketing digital y red de compradores activos para cerrar tu venta al mejor precio.",
        },
        {
          icon: "villa",
          image: one1Image,
          title: "Ver propiedades disponibles",
          description:
            "Explora el listado completo de propiedades en venta y renta. Encuentra tu hogar ideal con el respaldo de Realty One.",
          href: "https://veronicamoralespereda.myrealtyonegroup.com/",
        },
        {
          icon: "construction",
          image: appImage,
          title: "App de nuevas construcciones",
          description:
            "Explora casas y desarrollos nuevos en tu área. Accede a mi catálogo exclusivo de nuevas construcciones desde tu celular.",
          href: "https://a.nhb.app/u/veronica-moralespereda",
        },
      ],
    },
    why: {
      title: "¿Por qué elegirnos?",
      items: [
        { icon: "verified", text: "Agente certificada y afiliada a Realty One" },
        { icon: "location_on", text: "Especialista en la región de Tehuacán y Puebla" },
        { icon: "support_agent", text: "Atención personalizada en todo el proceso" },
        { icon: "trending_up", text: "Estrategia de precio basada en análisis de mercado" },
        { icon: "people", text: "Red de más de 500 compradores activos" },
        { icon: "lock", text: "Transacciones seguras y transparentes" },
      ],
    },
    testimonials: {
      title: "Lo que dicen nuestros clientes",
      cta: "Ver reseñas en Facebook",
    },
    properties: {
      heading: "Busquemos juntos",
      highlight: "la casa de tus sueños",
      sub: "Explora propiedades seleccionadas y conoce todo lo que hacemos por ti.",
      cta: "Ver propiedades",
    },
    videos: {
      title: "Nuestros Videos",
      cta: "Ver canal completo",
    },
    contactModal: {
      title: "¿Cómo prefieres contactarme?",
      whatsapp: "WhatsApp",
      sms: "Mensaje de texto",
      email: "Correo electrónico",
      emailSubject: "Quiero información sobre propiedades en Texas",
      emailBody:
        "Hola Veronica,\n\nMe gustaría recibir información sobre propiedades disponibles en Texas.\n\nPor favor contáctame cuando puedas.\n\nGracias.",
    },
    about: {
      sectionTitle: "Clientes",
      title: "¿Estás listo para",
      highlight: "contactarnos?",
      sub: "Elige tu medio favorito y te respondemos en menos de 24 horas.",
    },
    contact: {
      title: "¿Listo para dar el siguiente paso?",
      subtitle: "Contáctame y recibe asesoría personalizada sin costo. Estoy aquí para ayudarte.",
      cta: "Escríbeme por WhatsApp",
      phone: "(210) 383-1687",
      email: "VERONICAFAMILYREALTOR@GMAIL.COM",
      social: {
        facebook: "VeronicaFamilyRealtor",
        instagram: "@veronicafamilyrealtor",
      },
    },
  },
  en: {
    nav: { cta: "Schedule a Meeting" },
    hero: {
      title: "Veronica Family Realtor",
      slogan: "I'm your FRIEND, I'm your REALTOR",
      subtitle: "BUY OR SELL YOUR PROPERTY",
      subtitleHighlight: "IN TEXAS",
      cta: "Schedule a Meeting",
      ctaSub: "Response within 24 hours",
      videoLabel: "Learn more about us",
    },
    stats: [
      { value: "8+", label: "Years of experience" },
      { value: "200+", label: "Homes sold" },
      { value: "100%", label: "Satisfied clients" },
      { value: "24h", label: "Response time" },
    ],
    services: {
      title: "How we help you",
      subtitle: "Services designed for every stage of your real estate journey.",
      items: [
        {
          icon: "home",
          title: "Buy your home",
          description:
            "We guide you from search to closing and find the ideal property based on your budget and lifestyle.",
        },
        {
          icon: "sell",
          title: "Sell faster",
          description:
            "Professional valuation, digital marketing, and an active buyer network to close your sale at the best price.",
        },
        {
          icon: "villa",
          image: one1Image,
          title: "Browse available properties",
          description:
            "Explore the full listing of homes for sale and rent. Find your ideal property backed by Realty One.",
          href: "https://veronicamoralespereda.myrealtyonegroup.com/",
        },
        {
          icon: "construction",
          image: appImage,
          title: "New Construction App",
          description:
            "Browse new homes and developments in your area. Access my exclusive new construction catalog right from your phone.",
          href: "https://a.nhb.app/u/veronica-moralespereda",
        },
      ],
    },
    why: {
      title: "Why choose us?",
      items: [
        { icon: "verified", text: "Certified agent affiliated with Realty One" },
        { icon: "location_on", text: "Specialist in the Tehuacán and Puebla region" },
        { icon: "support_agent", text: "Personalized attention throughout the process" },
        { icon: "trending_up", text: "Pricing strategy based on market analysis" },
        { icon: "people", text: "Network of 500+ active buyers" },
        { icon: "lock", text: "Safe and transparent transactions" },
      ],
    },
    testimonials: {
      title: "What our clients say",
      cta: "View reviews on Facebook",
    },
    properties: {
      heading: "Let's find together",
      highlight: "the home of your dreams",
      sub: "Explore curated properties and discover everything we do for you.",
      cta: "View properties",
    },
    videos: {
      title: "Our Videos",
      cta: "View full channel",
    },
    contactModal: {
      title: "How would you like to contact me?",
      whatsapp: "WhatsApp",
      sms: "Text message",
      email: "Send an email",
      emailSubject: "I would like information about properties in Texas",
      emailBody:
        "Hi Veronica,\n\nI would like to receive information about available properties in Texas.\n\nPlease contact me when you can.\n\nThank you.",
    },
    about: {
      sectionTitle: "Clients",
      title: "Are you ready to",
      highlight: "contact us?",
      sub: "Choose your favorite channel and we'll respond within 24 hours.",
    },
    contact: {
      title: "Ready to take the next step?",
      subtitle: "Contact me and get free personalized advice. I'm here to help you.",
      cta: "Message me on WhatsApp",
      phone: "(210) 383-1687",
      email: "VERONICAFAMILYREALTOR@GMAIL.COM",
      social: {
        facebook: "VeronicaFamilyRealtor",
        instagram: "@veronicafamilyrealtor",
      },
    },
  },
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

function Presentation() {
  const [language, setLanguage] = useState("en");
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [contactAnchor, setContactAnchor] = useState(null);
  const videoRef = useRef(null);
  const bgVideoRef = useRef(null);
  const t = content[language];
  const emailHref = `mailto:VERONICAFAMILYREALTOR@GMAIL.COM?subject=${encodeURIComponent(
    t.contactModal.emailSubject
  )}&body=${encodeURIComponent(t.contactModal.emailBody)}`;

  useEffect(() => {
    const v = bgVideoRef.current;
    if (!v) return;
    v.muted = true;
    v.playsInline = true;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    ["loadedmetadata", "loadeddata", "canplay", "canplaythrough"].forEach((e) =>
      v.addEventListener(e, tryPlay)
    );
    return () => {
      ["loadedmetadata", "loadeddata", "canplay", "canplaythrough"].forEach((e) =>
        v.removeEventListener(e, tryPlay)
      );
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    video.playsInline = true;
    const tryPlay = () =>
      video
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => {});
    tryPlay();
    ["loadedmetadata", "loadeddata", "canplay", "canplaythrough"].forEach((e) =>
      video.addEventListener(e, tryPlay)
    );
    return () => {
      ["loadedmetadata", "loadeddata", "canplay", "canplaythrough"].forEach((e) =>
        video.removeEventListener(e, tryPlay)
      );
    };
  }, [language]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
  };

  return (
    <>
      {/* ── NAVBAR ── */}
      <DefaultNavbar
        routes={getRoutes(language)}
        action={{ type: "external", route: WHATSAPP_URL }}
        smsUrl={SMS_URL}
        language={language}
        onLanguageChange={setLanguage}
        sticky
      />

      {/* ── HERO ── */}
      <MKBox
        id="home"
        className={styles.hero}
        minHeight={{ xs: "100svh", md: "85vh" }}
        width="100%"
      >
        <MKBox
          component="video"
          ref={bgVideoRef}
          src={bgImage}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className={styles.heroBgVideo}
        />

        <MKBox className={styles.heroOverlay} />

        <Container
          maxWidth="xl"
          sx={{ pt: { xs: "100px", md: 0 }, position: "relative", zIndex: 2 }}
        >
          <Grid container spacing={{ xs: 3, md: 6 }} alignItems="center">
            {/* Left: text + logos */}
            <Grid item xs={12} md={6}>
              <MKBox px={{ xs: 1, sm: 2 }} sx={{ textAlign: { xs: "center", md: "left" } }}>
                <MKBox
                  display="flex"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={{ xs: 2, md: 3 }}
                  mb={{ xs: 3, md: 4 }}
                  justifyContent={{ xs: "center", md: "flex-start" }}
                >
                  <MKBox
                    component="img"
                    src={logoVeronica}
                    alt="Veronica Realtor"
                    className={styles.logoVeronica}
                  />
                  <MKBox
                    component="img"
                    src={logoOne}
                    alt="Realty One"
                    className={styles.logoRealty}
                  />
                </MKBox>

                <MKTypography
                  variant="h1"
                  color="white"
                  fontWeight="bold"
                  mb={1}
                  className={styles.heroTitle}
                >
                  {t.hero.title}
                </MKTypography>

                <MKTypography
                  component="p"
                  mb={2}
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    fontStyle: "italic",
                    fontSize: { xs: "1rem", md: "1.2rem" },
                    letterSpacing: "0.03em",
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  {t.hero.slogan}
                </MKTypography>

                <MKTypography
                  color="white"
                  mb={4}
                  className={styles.heroSubtitle}
                  sx={{ display: "block" }}
                >
                  {t.hero.subtitle}
                  <br />
                  <MKTypography
                    component="span"
                    sx={{
                      fontSize: { xs: "2.4rem", md: "4.5rem" },
                      fontWeight: "bold",
                      background: "linear-gradient(90deg, #facc15 0%, #f59e0b 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      display: "block",
                      lineHeight: 1.1,
                    }}
                  >
                    {t.hero.subtitleHighlight}
                  </MKTypography>
                </MKTypography>

                <MKBox
                  mb={3}
                  display="flex"
                  flexDirection="column"
                  alignItems={{ xs: "center", md: "flex-start" }}
                >
                  <MKButton
                    variant="gradient"
                    color="success"
                    size="large"
                    onClick={(e) => setContactAnchor(e.currentTarget)}
                    sx={{
                      px: { xs: 3, md: 5 },
                      py: { xs: 1.5, md: 2 },
                      fontSize: { xs: "0.95rem", md: "1.05rem" },
                      borderRadius: "50px",
                      fontWeight: "bold",
                      boxShadow: "0 8px 32px rgba(56,182,100,0.45)",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Icon>calendar_month</Icon>
                    {t.hero.cta}
                  </MKButton>

                  <Popover
                    open={Boolean(contactAnchor)}
                    anchorEl={contactAnchor}
                    onClose={() => setContactAnchor(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    transformOrigin={{ vertical: "top", horizontal: "center" }}
                    PaperProps={{
                      sx: {
                        borderRadius: 3,
                        p: 2.5,
                        mt: 1,
                        minWidth: 260,
                        backgroundColor: "#fff",
                        boxShadow: "0 16px 48px rgba(0,0,0,0.45)",
                        border: "1px solid rgba(0,0,0,0.08)",
                      },
                    }}
                  >
                    <MKTypography variant="subtitle2" fontWeight="bold" textAlign="center" mb={2}>
                      {t.contactModal.title}
                    </MKTypography>
                    <MKBox display="flex" flexDirection="column" gap={1.5}>
                      <MKButton
                        component="a"
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="gradient"
                        color="success"
                        fullWidth
                        onClick={() => setContactAnchor(null)}
                        sx={{ borderRadius: "50px", display: "flex", gap: 1.5, py: 1.2 }}
                      >
                        <MKBox
                          component="img"
                          src={whatsappIcon}
                          alt="WhatsApp"
                          sx={{ width: 22, height: 22 }}
                        />
                        {t.contactModal.whatsapp}
                      </MKButton>
                      <MKButton
                        component="a"
                        href={SMS_URL}
                        variant="outlined"
                        color="dark"
                        fullWidth
                        onClick={() => setContactAnchor(null)}
                        sx={{ borderRadius: "50px", display: "flex", gap: 1.5, py: 1.2 }}
                      >
                        <MKBox
                          component="img"
                          src={smsIcon}
                          alt="SMS"
                          sx={{ width: 22, height: 22 }}
                        />
                        {t.contactModal.sms}
                      </MKButton>
                      <MKButton
                        component="a"
                        href={emailHref}
                        variant="gradient"
                        color="info"
                        fullWidth
                        onClick={() => setContactAnchor(null)}
                        sx={{ borderRadius: "50px", display: "flex", gap: 1.5, py: 1.2 }}
                      >
                        <Icon sx={{ fontSize: "1.3rem !important" }}>email</Icon>
                        {t.contactModal.email}
                      </MKButton>
                    </MKBox>
                  </Popover>

                  <MKTypography color="white" mt={1} className={styles.ctaHint}>
                    {t.hero.ctaSub}
                  </MKTypography>
                </MKBox>
              </MKBox>
            </Grid>

            {/* Right: video player */}
            <Grid item xs={12} md={6}>
              <MKBox className={styles.videoWrapper}>
                <MKBox className={styles.videoBadge}>
                  <MKBox className={styles.videoBadgeDot} />
                  <span className={styles.videoBadgeText}>{t.hero.videoLabel}</span>
                </MKBox>

                <MKBox
                  component="video"
                  key={language}
                  ref={videoRef}
                  src={VIDEOS[language]}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  className={styles.videoFill}
                />

                <MKBox
                  className={styles.playBtn}
                  onClick={togglePlay}
                  style={{ opacity: isPlaying ? 0 : 1 }}
                >
                  <Icon className={styles.playIcon}>{isPlaying ? "pause" : "play_arrow"}</Icon>
                </MKBox>

                <MKBox className={styles.muteBtn} onClick={toggleMute}>
                  <Icon className={styles.muteIcon}>{isMuted ? "volume_off" : "volume_up"}</Icon>
                </MKBox>
              </MKBox>
            </Grid>
          </Grid>
        </Container>
      </MKBox>

      {/* ── MAIN CARD ── */}
      <Card
        sx={{
          p: { xs: 1, md: 2 },
          mx: { xs: 1.5, sm: 2, lg: 3 },
          mt: { xs: -4, md: -8 },
          mb: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.92),
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
          overflow: "hidden",
        }}
      >
        {/* ── STATS + YOUTUBE ── */}
        <MKBox
          id="properties"
          py={{ xs: 4, md: 6 }}
          px={{ xs: 2, md: 4 }}
          sx={{ backgroundColor: "#d8dadf" }}
        >
          {/* Section heading */}
          <MKBox textAlign="center" mb={{ xs: 5, md: 7 }}>
            <MKTypography
              variant="h2"
              fontWeight="bold"
              sx={{
                fontSize: { xs: "1.8rem", md: "2.8rem" },
                lineHeight: 1.15,
                letterSpacing: "-0.5px",
              }}
            >
              {t.properties.heading}{" "}
              <MKTypography
                component="span"
                variant="h2"
                fontWeight="bold"
                sx={{
                  fontSize: "inherit",
                  background: "linear-gradient(90deg, #1a73e8 0%, #0d47a1 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {t.properties.highlight}
              </MKTypography>
            </MKTypography>
            <MKBox
              sx={{
                width: { xs: 60, md: 80 },
                height: 4,
                borderRadius: 2,
                background: "linear-gradient(90deg, #1a73e8, #0d47a1)",
                mx: "auto",
                mt: 2,
              }}
            />
          </MKBox>

          <Grid container spacing={{ xs: 4, md: 5 }} alignItems="center">
            {/* Left: YouTube */}
            <Grid item xs={12} md={6}>
              <MKBox display="flex" alignItems="center" gap={0.8} mb={2}>
                <MKBox className={styles.ytBadge}>
                  <Icon className={styles.ytIcon}>smart_display</Icon>
                  <span className={styles.ytLabel}>YouTube</span>
                </MKBox>
                <MKTypography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ fontSize: { xs: "1rem", md: "1.1rem" } }}
                >
                  {t.videos.title}
                </MKTypography>
              </MKBox>

              <MKBox className={styles.ytEmbed}>
                <MKBox
                  component="iframe"
                  src={`https://www.youtube.com/embed?listType=playlist&list=${YOUTUBE_PLAYLIST_ID}&rel=0&modestbranding=1`}
                  title={t.videos.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className={styles.ytIframe}
                />
              </MKBox>

              <MKBox mt={1.5}>
                <MKButton
                  component="a"
                  href={YOUTUBE_CHANNEL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  fullWidth
                  className={styles.ytChannelBtn}
                >
                  <Icon className={styles.ytChannelIcon}>smart_display</Icon>
                  {t.videos.cta}
                </MKButton>
              </MKBox>
            </Grid>

            {/* Right: Stats */}
            <Grid item xs={12} md={6}>
              <ExpandingOptions items={t.stats} />
              <MKBox mt={3} textAlign="center">
                <MKButton
                  component="a"
                  href="https://veronicamoralespereda.myrealtyonegroup.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="gradient"
                  color="info"
                  size="large"
                  fullWidth
                  sx={{
                    borderRadius: "50px",
                    py: 1.5,
                    fontWeight: "bold",
                    fontSize: { xs: "0.95rem", md: "1rem" },
                    boxShadow: "0 8px 24px rgba(26,115,232,0.35)",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Icon>villa</Icon>
                  {t.properties.cta}
                </MKButton>
              </MKBox>
            </Grid>
          </Grid>
        </MKBox>

        {/* ── SERVICES ── */}
        <MKBox id="services" py={{ xs: 3, md: 6 }} px={{ xs: 2, md: 4 }}>
          <MKBox textAlign="center" mb={{ xs: 4, md: 6 }}>
            <MKTypography
              variant="h2"
              fontWeight="bold"
              textAlign="center"
              display="block"
              sx={{
                fontSize: { xs: "1.6rem", md: "2.2rem" },
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 60%, #42a5f5 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: { xs: 0.5, md: 1 },
              }}
            >
              {t.services.title}
            </MKTypography>
            <MKBox
              sx={{
                width: { xs: 60, md: 80 },
                height: 4,
                borderRadius: 2,
                background: "linear-gradient(90deg, #1a73e8, #42a5f5)",
                mx: "auto",
                mb: { xs: 2, md: 3 },
              }}
            />
            <MKTypography
              variant="body1"
              color="text"
              sx={{ maxWidth: "560px", mx: "auto", fontSize: { xs: "0.95rem", md: "1.05rem" } }}
            >
              {t.services.subtitle}
            </MKTypography>
          </MKBox>
          <Grid container spacing={{ xs: 2, md: 3 }} alignItems="stretch">
            {/* 1/4 — Autorizada panel */}
            <Grid item xs={12} md={3}>
              <div className={serviceStyles.panel}>
                <span className={serviceStyles.panelLabel}>
                  {language === "es" ? "Autorizada" : "Authorized"}
                </span>
                <img
                  className={serviceStyles.panelLogoLgColor}
                  src={realtyOneLogo}
                  alt="Realty One"
                />
                <img className={serviceStyles.panelLogoSm} src={realtorLogo} alt="REALTOR®" />
                <div className={serviceStyles.panelSeparator} />
                <div className={serviceStyles.panelLogosRow}>
                  <img className={serviceStyles.panelLogoIcon} src={mlsLogo} alt="MLS" />
                  <img
                    className={serviceStyles.panelLogoIcon}
                    src={equalLogo}
                    alt="Equal Housing"
                  />
                </div>
              </div>
            </Grid>

            {/* 3/4 — Service cards */}
            <Grid item xs={12} md={9}>
              <Grid container spacing={{ xs: 2, md: 3 }} height="100%">
                {t.services.items.map((item, i) => (
                  <Grid key={i} item xs={12} sm={6}>
                    <ServiceCard {...item} />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </MKBox>

        {/* ── ABOUT / GALLERY ── */}
        <MKBox
          id="about"
          py={{ xs: 5, md: 8 }}
          px={{ xs: 2, md: 4 }}
          sx={{ backgroundColor: "#f5f6f8", borderRadius: 3, mx: { xs: 0, md: 1 } }}
        >
          <MKTypography
            variant="h2"
            fontWeight="bold"
            textAlign="center"
            display="block"
            sx={{
              fontSize: { xs: "1.6rem", md: "2.2rem" },
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 60%, #42a5f5 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: { xs: 0.5, md: 1 },
            }}
          >
            {t.about.sectionTitle}
          </MKTypography>
          <MKBox
            sx={{
              width: { xs: 60, md: 80 },
              height: 4,
              borderRadius: 2,
              background: "linear-gradient(90deg, #1a73e8, #42a5f5)",
              mx: "auto",
              mb: { xs: 4, md: 6 },
            }}
          />
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            {/* Left: carousel */}
            <Grid item xs={12} md={6}>
              <MorphGallery />
            </Grid>

            {/* Right: CTA + social icons */}
            <Grid item xs={12} md={6}>
              <MKBox textAlign={{ xs: "center", md: "left" }}>
                <MKTypography
                  variant="h2"
                  fontWeight="bold"
                  sx={{ fontSize: { xs: "2rem", md: "2.8rem" }, lineHeight: 1.15, mb: 1 }}
                >
                  {t.about.title}{" "}
                  <MKTypography
                    component="span"
                    variant="h2"
                    fontWeight="bold"
                    sx={{
                      fontSize: "inherit",
                      background: "linear-gradient(90deg, #1a73e8 0%, #0d47a1 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {t.about.highlight}
                  </MKTypography>
                </MKTypography>

                <MKTypography
                  variant="body1"
                  color="text"
                  sx={{ mb: 4, fontSize: { xs: "0.95rem", md: "1.05rem" } }}
                >
                  {t.about.sub}
                </MKTypography>

                <Grid container spacing={2} justifyContent={{ xs: "center", md: "flex-start" }}>
                  {/* Phone */}
                  <Grid item>
                    <a href="tel:+12103831687" className={aboutStyles.socialBtn}>
                      <div className={`${aboutStyles.circle} ${aboutStyles.circlePhone}`}>
                        <Icon sx={{ color: "#fff", fontSize: "1.8rem" }}>phone</Icon>
                      </div>
                      <MKTypography variant="caption" fontWeight="bold" color="text">
                        {language === "es" ? "Llamar" : "Call"}
                      </MKTypography>
                    </a>
                  </Grid>

                  {/* Facebook */}
                  <Grid item>
                    <a
                      href="https://www.facebook.com/VeronicaFamilyRealtor"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={aboutStyles.socialBtn}
                    >
                      <div className={`${aboutStyles.circle} ${aboutStyles.circleFacebook}`}>
                        <img src={facebookImg} alt="Facebook" className={aboutStyles.iconImg} />
                      </div>
                      <MKTypography variant="caption" fontWeight="bold" color="text">
                        Facebook
                      </MKTypography>
                    </a>
                  </Grid>

                  {/* WhatsApp */}
                  <Grid item>
                    <a
                      href={WHATSAPP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={aboutStyles.socialBtn}
                    >
                      <div className={`${aboutStyles.circle} ${aboutStyles.circleWhatsApp}`}>
                        <img src={whatsappIcon} alt="WhatsApp" className={aboutStyles.iconImg} />
                      </div>
                      <MKTypography variant="caption" fontWeight="bold" color="text">
                        WhatsApp
                      </MKTypography>
                    </a>
                  </Grid>

                  {/* SMS */}
                  <Grid item>
                    <a href={SMS_URL} className={aboutStyles.socialBtn}>
                      <div className={`${aboutStyles.circle} ${aboutStyles.circleSms}`}>
                        <img src={smsIcon} alt="SMS" className={aboutStyles.iconImg} />
                      </div>
                      <MKTypography variant="caption" fontWeight="bold" color="text">
                        SMS
                      </MKTypography>
                    </a>
                  </Grid>

                  {/* Instagram */}
                  <Grid item>
                    <a
                      href="https://www.instagram.com/veronicafamilyrealtor/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={aboutStyles.socialBtn}
                    >
                      <img src={instagramImg} alt="Instagram" className={aboutStyles.iconImgLg} />
                      <MKTypography variant="caption" fontWeight="bold" color="text">
                        Instagram
                      </MKTypography>
                    </a>
                  </Grid>
                </Grid>
              </MKBox>
            </Grid>
          </Grid>
        </MKBox>

        {/* ── TESTIMONIALS ── */}
        <MKBox id="testimonials" py={{ xs: 3, md: 6 }} px={{ xs: 2, md: 4 }}>
          <MKBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
            mb={{ xs: 3, md: 5 }}
          >
            <MKBox className={styles.fbBadge}>
              <Icon className={styles.fbIcon}>thumb_up</Icon>
              <span className={styles.fbLabel}>Facebook</span>
            </MKBox>
            <MKTypography
              variant="h3"
              fontWeight="bold"
              sx={{ fontSize: { xs: "1.6rem", md: "2.2rem" } }}
            >
              {t.testimonials.title}
            </MKTypography>
          </MKBox>

          <MKBox sx={{ maxWidth: 860, mx: "auto" }}>
            <div
              className="elfsight-app-fa0408c3-af8b-45a4-9875-c575e95202fc"
              data-elfsight-app-lazy
            />
          </MKBox>

          <MKBox textAlign="center" mt={2.5}>
            <MKButton
              component="a"
              href="https://www.facebook.com/VeronicaFamilyRealtor/reviews"
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              size="small"
              className={styles.fbChannelBtn}
            >
              <Icon className={styles.fbChannelIcon}>open_in_new</Icon>
              {t.testimonials.cta}
            </MKButton>
          </MKBox>
        </MKBox>

        {/* ── CONTACT CTA ── */}
        <MKBox
          id="contact"
          py={{ xs: 6, md: 10 }}
          px={{ xs: 2, md: 6 }}
          sx={{
            background: "linear-gradient(160deg, #0d2b6b 0%, #1a3a8a 60%, #1565c0 100%)",
            borderRadius: 3,
            mx: { xs: 1, md: 3 },
            mb: { xs: 3, md: 4 },
          }}
        >
          {/* Title */}
          <MKBox textAlign="center" mb={{ xs: 5, md: 7 }}>
            <MKTypography
              variant="h2"
              fontWeight="bold"
              sx={{
                color: "#fff",
                fontSize: { xs: "1.6rem", md: "2.4rem" },
                letterSpacing: "-0.5px",
                mb: 1,
              }}
            >
              {t.contact.title}
            </MKTypography>
            <MKBox
              sx={{
                width: { xs: 60, md: 80 },
                height: 4,
                borderRadius: 2,
                background: "linear-gradient(90deg, #42a5f5, #90caf9)",
                mx: "auto",
                mt: 1.5,
                mb: 2.5,
              }}
            />
            <MKTypography
              variant="body1"
              sx={{
                color: "rgba(255,255,255,0.72)",
                maxWidth: "520px",
                mx: "auto",
                fontSize: { xs: "0.95rem", md: "1.05rem" },
              }}
            >
              {t.contact.subtitle}
            </MKTypography>
          </MKBox>

          {/* Contact cards */}
          <Grid container spacing={{ xs: 2, md: 3 }} justifyContent="center">
            {/* Phone */}
            <Grid item xs={12} sm={6} md>
              <a
                href="tel:+12103831687"
                className={`${contactStyles.card} ${contactStyles.cardDefault}`}
              >
                <div className={`${contactStyles.circle} ${contactStyles.circleDefault}`}>
                  <Icon className={contactStyles.cardIcon}>phone</Icon>
                </div>
                <span className={contactStyles.cardLabel}>
                  {language === "es" ? "Teléfono" : "Phone"}
                </span>
                <span className={contactStyles.cardValue}>{t.contact.phone}</span>
              </a>
            </Grid>

            {/* WhatsApp */}
            <Grid item xs={12} sm={6} md>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`${contactStyles.card} ${contactStyles.cardWhatsApp}`}
              >
                <div className={`${contactStyles.circle} ${contactStyles.circleWhatsApp}`}>
                  <img src={whatsappIcon} alt="WhatsApp" className={contactStyles.iconImg} />
                </div>
                <span className={contactStyles.cardLabel}>WhatsApp</span>
                <span className={contactStyles.cardValue}>{t.contact.phone}</span>
              </a>
            </Grid>

            {/* SMS */}
            <Grid item xs={12} sm={6} md>
              <a href={SMS_URL} className={`${contactStyles.card} ${contactStyles.cardDefault}`}>
                <div className={`${contactStyles.circle} ${contactStyles.circleDefault}`}>
                  <img src={smsIcon} alt="SMS" className={contactStyles.iconImg} />
                </div>
                <span className={contactStyles.cardLabel}>
                  {language === "es" ? "Mensaje de texto" : "Text message"}
                </span>
                <span className={contactStyles.cardValue}>{t.contact.phone}</span>
              </a>
            </Grid>

            {/* Email */}
            <Grid item xs={12} sm={6} md>
              <a
                href={`mailto:${t.contact.email}`}
                className={`${contactStyles.card} ${contactStyles.cardDefault}`}
              >
                <div className={`${contactStyles.circle} ${contactStyles.circleDefault}`}>
                  <Icon className={contactStyles.cardIcon}>email</Icon>
                </div>
                <span className={contactStyles.cardLabel}>
                  {language === "es" ? "Correo" : "Email"}
                </span>
                <span className={contactStyles.cardValue}>{t.contact.email}</span>
              </a>
            </Grid>

            {/* Facebook */}
            <Grid item xs={12} sm={6} md>
              <a
                href="https://www.facebook.com/VeronicaFamilyRealtor"
                target="_blank"
                rel="noopener noreferrer"
                className={`${contactStyles.card} ${contactStyles.cardFacebook}`}
              >
                <div className={`${contactStyles.circle} ${contactStyles.circleFacebook}`}>
                  <img src={facebookImg} alt="Facebook" className={contactStyles.iconImg} />
                </div>
                <span className={contactStyles.cardLabel}>Facebook</span>
                <span className={contactStyles.cardValue}>{t.contact.social.facebook}</span>
              </a>
            </Grid>

            {/* Instagram */}
            <Grid item xs={12} sm={6} md>
              <a
                href="https://www.instagram.com/veronicafamilyrealtor/"
                target="_blank"
                rel="noopener noreferrer"
                className={`${contactStyles.card} ${contactStyles.cardInstagram}`}
              >
                <div className={`${contactStyles.circle} ${contactStyles.circleInstagram}`}>
                  <img src={instagramImg} alt="Instagram" className={contactStyles.iconImgLg} />
                </div>
                <span className={contactStyles.cardLabel}>Instagram</span>
                <span className={contactStyles.cardValue}>{t.contact.social.instagram}</span>
              </a>
            </Grid>
          </Grid>
        </MKBox>
      </Card>

      {/* ── FOOTER ── */}
      <MKBox pt={4} px={1} mt={4}>
        <DefaultFooter content={getFooterRoutes(language)} />
      </MKBox>

      <ScrollToTop />
    </>
  );
}

export default Presentation;
